import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  TextField,
  Grid,
  Card,
  CardContent,
  Chip,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  IconButton,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  KeyboardArrowLeft as ArrowLeftIcon,
  KeyboardArrowRight as ArrowRightIcon,
  Facebook as FacebookIcon,
  LinkedIn as LinkedInIcon,
} from "@mui/icons-material";
import CareerInsightsSection from "./HomePage/CareerInsightsSection";
import HowItWorksSection from "./HowItWorksSection";
import RelevanceTimelineSection from "./RelevanceTimelineSection";
import FAQSection from "./common/FAQ";
import WhoIsThisFor from "./common/Who-Is-This-For-Section";
import AnnualIncome from "./common/AnnualIncome";
import WhyUseThisToolSection from "./WhyUseThisToolSection";
import SignInModal from "../auth/components/SignIn";
import CareerPath from "./CareerPath";
import InterviewQuestionSection from "./common/InterviewQuestionSection";
import HomePageFooter from "./common/HomePageFooter";
import BasicRelevanceCheck from "./common/BasicRelevanceCheck";
import BasicRelevanceCheckDark from "./common/BasicRelevanceCheckDark";
import { useAuthRedirect } from "../hooks/useAuthRedirect";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  parseSkillsFromUrl,
  storeSkillsInLocalStorage,
  clearUrlParameters,
} from "../utils/skillsUtils";
import ReactGA4 from "react-ga4";

const HomePage = () => {
  const navigate = useNavigate();
  const [topSkill, setTopSkill] = useState("");
  const [location, setLocation] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [tabValue, setTabValue] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [isScrolled, setIsScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [userData, setUserData] = useState(null);
  const isLoggedIn = Boolean(
    useSelector((state) => state?.auth?.isAuthenticated)
  );

  // Handle social login redirects
  useAuthRedirect();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Handle skills from URL query string and clean up URL parameters
  useEffect(() => {
    const skillsFromUrl = parseSkillsFromUrl();

    if (skillsFromUrl.length > 0) {
      // Store skills in localStorage
      storeSkillsInLocalStorage(skillsFromUrl, "url-query-redirect");

      // If user is already logged in, redirect to skill-relevance page
      if (isLoggedIn) {
        navigate("/skill-relevance");
      } else {
        // If user is not logged in, show sign-in popup
        setOpen(true);
      }
    }

    // Always clean up URL parameters (skills, auth codes, etc.)
    clearUrlParameters();
  }, [isLoggedIn, navigate]);

  // Handle scroll effect for header
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setIsScrolled(scrollTop > 0);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleOpenModal = () => {
    if (isLoggedIn) {
      window.location.replace("/skill-relevance");
    } else {
      ReactGA4.event("signin_click");
      setOpen(true);
    }
  };

  const handleStartClick = () => {
    if (isLoggedIn) {
      ReactGA4.event("view_full_insights_click");
      navigate("/skill-relevance");
    } else {
      // Scroll to the relevance section instead of opening login popup
      const relevanceSection = document.getElementById(
        "check-relevance-section-dark"
      );
      if (relevanceSection) {
        // Account for fixed header height on mobile
        const headerOffset = window.innerWidth < 600 ? 80 : 0;
        const elementPosition = relevanceSection.getBoundingClientRect().top;
        const offsetPosition =
          elementPosition + window.pageYOffset - headerOffset;

        window.scrollTo({
          top: offsetPosition,
          behavior: "smooth",
        });
      }
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: "#000",
        color: "#fff",
        overflow: { xs: "hidden", md: "unset" },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          backgroundColor: isScrolled ? "#000" : "transparent",
          borderBottom: isScrolled ? "1px solid #333" : "none",
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          transition: "all 0.3s ease",
          backdropFilter: isScrolled ? "blur(10px)" : "none",
        }}
      >
        <Container maxWidth="xl">
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box display="flex" alignItems="center">
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: { xs: 1, md: 2 },
                }}
              >
                <Box
                  sx={{
                    p: { xs: 1, md: 2 },
                    display: "flex",
                    justifyContent: "center",
                    maxWidth: { xs: "60px", sm: "70px", md: "80px" },
                  }}
                >
                  <a href="/">
                    <img
                      src={"/assets/images/TrueSelfy-logo-white.svg"}
                      alt="true-selfy"
                      loading="lazy"
                      style={{
                        maxWidth: "100%",
                        width: "100%",
                      }}
                    />
                  </a>
                </Box>
              </Box>
            </Box>
            {!isLoggedIn ? (
              <Button
                variant="contained"
                onClick={handleOpenModal}
                sx={{
                  backgroundColor: "transparent !important",
                  backgroundImage:
                    "linear-gradient(to right, rgba(74,61,158,1), rgba(108,82,229,1) )",
                  color: "#fff",
                  width: { xs: "100px", sm: "120px", md: "139px" },
                  height: { xs: "40px", sm: "45px", md: "50px" },
                  borderRadius: "25px",
                  textTransform: "none",
                  fontWeight: 700,
                  fontStyle: "bold",
                  fontSize: {
                    xs: "14px",
                    sm: "16px",
                    md: "18px",
                    lg: "20px",
                  },
                  lineHeight: {
                    xs: "21px",
                    sm: "24px",
                    md: "27px",
                    lg: "30px",
                  },
                  fontFamily:
                    "'Montserrat', 'SF Pro Display', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                  transition: "all 0.3s linear",
                  "&:hover": {
                    border: "1px solid rgba(74,61,158,1)",
                    background: "#FFF !important",
                    color: "rgba(108,82,229,1)",
                    transition: "all 0.3s linear",
                  },
                }}
              >
                Sign In
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleStartClick}
                sx={{
                  backgroundColor: "transparent !important",
                  backgroundImage:
                    "linear-gradient(to right, rgba(74,61,158,1), rgba(108,82,229,1) )",
                  color: "#fff",
                  minWidth: { xs: "140px", sm: "160px", md: "180px" },
                  height: { xs: "40px", sm: "45px", md: "50px" },
                  borderRadius: "25px",
                  textTransform: "none",
                  fontWeight: 700,
                  fontStyle: "bold",
                  fontSize: {
                    xs: "13px",
                    sm: "15px",
                    md: "17px",
                    lg: "19px",
                  },
                  lineHeight: {
                    xs: "21px",
                    sm: "24px",
                    md: "27px",
                    lg: "30px",
                  },
                  px: { xs: 2, sm: 2.5, md: 3 },
                  fontFamily:
                    "'Montserrat', 'SF Pro Display', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                  transition: "all 0.3s linear",
                  "&:hover": {
                    border: "1px solid rgba(74,61,158,1)",
                    background: "#FFF !important",
                    color: "rgba(108,82,229,1)",
                    transition: "all 0.3s linear",
                  },
                }}
              >
                Career Insights
              </Button>
            )}
          </Box>
        </Container>
      </Box>

      {/* Hero Section */}
      <Box
        sx={{
          width: "100%",
          minHeight: {
            xs: "280px",
            sm: "320px",
            md: "360px",
            lg: "380px",
            xl: "400px",
          },
          maxHeight: {
            xs: "50vh",
            sm: "55vh",
            md: "60vh",
            lg: "65vh",
            xl: "70vh",
          },
          opacity: 1,
          position: "relative",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          pt: { xs: "70px", sm: "80px", md: "90px", lg: "100px" },
          pb: { xs: 1, sm: 1.5, md: 1.5, lg: 2 },
        }}
      >
        {/* Video Background */}
        <video
          autoPlay
          muted
          loop
          playsInline
          aria-label="Animated bulb effect background video"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            zIndex: 1,
          }}
        >
          <source
            src="https://trueselfy-assets.s3.ap-southeast-2.amazonaws.com/bulbeffect.mp4"
            type="video/mp4"
          />
          Your browser does not support the video tag.
        </video>

        {/* Overlay for better text readability */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.4)",
            zIndex: 2,
          }}
        />

        {/* Content */}
        <Container
          maxWidth="xl"
          sx={{
            position: "relative",
            zIndex: 3,
            display: "flex",
            flexDirection: "column",
            minHeight: "100%",
            justifyContent: "center",
            py: { xs: 1, sm: 1.5, md: 1.5, lg: 2 },
          }}
        >
          <Grid
            container
            alignItems="center"
            justifyContent="center"
            sx={{ flex: "1 1 auto", minHeight: 0 }}
          >
            <Grid item xs={12} md={10} lg={8}>
              <Box
                sx={{
                  color: "#fff",
                  maxWidth: { xs: "100%", sm: "90%", md: "85%", lg: "100%" },
                  px: { xs: 2, sm: 2.5, md: 3, lg: 3, xl: 2 },
                  textAlign: "center",
                }}
              >
                <Typography
                  variant="h1"
                  sx={{
                    fontWeight: 600,
                    fontStyle: "bold",
                    fontSize: {
                      xs: "22px",
                      sm: "32px",
                      md: "38px",
                      lg: "48px",
                    },
                    lineHeight: {
                      xs: "26px",
                      sm: "40px",
                      md: "48px",
                      lg: "60px",
                    },
                    marginBottom: { xs: 0.5, sm: 0.75, md: 1, lg: 1.25 },
                    color: "#fff",
                    fontFamily:
                      "'SF Pro Display', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                    letterSpacing: { xs: "-0.02em", md: "-0.01em" },
                    textAlign: "center",
                  }}
                >
                  Your Career Growth Starts Here <br />
                  Your Personalized Career Roadmap & <br />
                  AI-Powered Guidance
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 500,
                    fontSize: {
                      xs: "11px",
                      sm: "14px",
                      md: "16px",
                      lg: "18px",
                    },
                    lineHeight: {
                      xs: "16px",
                      sm: "20px",
                      md: "24px",
                      lg: "26px",
                    },
                    fontStyle: "medium",
                    color: "#fff",
                    marginBottom: 0,
                    fontFamily:
                      "'SF Pro Display', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                    maxWidth: "100%",
                    textAlign: "center",
                  }}
                >
                  Whether you're a fresher, switching jobs, or seeking
                  mid-career clarity, our Career Guide (AI Powered) builds a
                  career blueprint tailored to your skills, goals, and future
                  skill trends. Unlock your career potential with actionable
                  skill mapping, upskilling paths, and a personalized career
                  journey designed for long-term success.
                </Typography>
                {/* <Button
                  variant="contained"
                  onClick={handleStartClick}
                  sx={{
                    color: "#fff",
                    borderRadius: "30px",
                    textTransform: "none",
                    fontWeight: 700,
                    py: { xs: "8px", sm: "9px", md: "10px" },
                    px: { xs: "32px", sm: "36px", md: "40px" },
                    fontStyle: "bold",
                    fontSize: {
                      xs: "13px",
                      sm: "15px",
                      md: "17px",
                      lg: "19px",
                    },
                    lineHeight: {
                      xs: "19px",
                      sm: "22px",
                      md: "25px",
                      lg: "28px",
                    },
                    backgroundColor: "transparent !important",
                    backgroundImage:
                      "linear-gradient(to right, rgba(74,61,158,1), rgba(108,82,229,1) )",
                    "&:hover": {
                      border: "1px solid rgba(74,61,158,1)",
                      background: "#FFF !important",
                      color: "rgba(108,82,229,1)",
                      transition: "all 0.3s linear",
                    },
                    fontFamily:
                      "'Montserrat', 'SF Pro Display', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                  }}
                >
                  Start
                </Button> */}
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Basic Relevance Check Section */}
      <BasicRelevanceCheckDark
        isLoggedIn={isLoggedIn}
        onLoginClick={() => {
          ReactGA4.event("signin_click");
          setOpen(true);
        }}
        onViewFullInsights={() => {
          navigate("/skill-relevance");
        }}
      />

      {/* Why Use This Tool Section */}
      <WhyUseThisToolSection />

      <WhoIsThisFor />

      {/* How It Works Section */}
      <HowItWorksSection />

      {/* Relevance Timeline Section */}
      <RelevanceTimelineSection />

      {/* SignIn Modal */}
      <SignInModal setOpen={setOpen} open={open} userData={userData} />

      {/* Career Insights Section */}
      <CareerInsightsSection />

      {/* Career Path Section */}
      <CareerPath />

      {/* Annual Income Section */}
      <AnnualIncome />

      {/* Interview Questions Section */}
      <InterviewQuestionSection />

      <FAQSection />

      {/* Footer */}
      <HomePageFooter />
    </Box>
  );
};

export default HomePage;
